import React, { forwardRef, InputHTMLAttributes, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { HTMLInputElement } from 'window-or-global';
import Icon, { IconName } from '../../icons/icon/icon-component';
import styles from './text-component.module.scss';

type TextProps = {
  label?: string;
  name?: string;
  helpText?: string;
  icon?: IconName;
  /** only considered if props.icon is initialised */
  secondIcon?: IconName;
  alignIcon?: 'left' | 'right';
  iconWidth?: number;
  iconHeight?: number;
  onClickIcon?: () => void;
  background?: 'white' | 'grey';
  errorMessage?: JSX.Element | string;
  isValid?: boolean;
  showEllipsesOnOverflow?: boolean;
  validationCallback?: (value: string) => boolean | Promise<boolean>;
  showRequiredAsterisk?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

const DEFAULT_PROPS: Required<Pick<TextProps, 'alignIcon' | 'iconWidth' | 'iconHeight'>> = {
  alignIcon: 'left',
  iconWidth: 24,
  iconHeight: 24,
};

export interface TextRef {
  focus(): void;
  blur(): void;
  checkValidity(): boolean;
}

/**
 * Text component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=14%3A37
 */
export const Text = forwardRef<TextRef, TextProps>((props: TextProps, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    label,
    name,
    helpText,
    icon,
    secondIcon,
    alignIcon,
    iconWidth,
    iconHeight,
    background,
    className,
    errorMessage,
    isValid,
    showEllipsesOnOverflow,
    onClickIcon,
    validationCallback,
    onBlur,
    ...inputAttributes
  } = { ...DEFAULT_PROPS, ...props }; // <-- merge defaults before destructuring;

  const [isValidState, setIsValidState] = useState(props.isValid ?? true);

  // exposes internal input methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },

    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },

    checkValidity: (): boolean => {
      if (inputRef.current) {
        return inputRef.current.checkValidity();
      }

      return false;
    },
  }));

  // update state if props.isValid changes
  useEffect(() => {
    setIsValidState(props.isValid ?? true);
  }, [props.isValid]);

  return (
    <div className={`${styles['text-component']} ${props.className ? props.className : ''}`}>
      {props.label ? (
        <label htmlFor={props.name}>
          {props.label}
          {props.showRequiredAsterisk && <span className={styles.required}> *</span>}
        </label>
      ) : null}

      {props.helpText ? <p>{props.helpText}</p> : null}

      <div
        className={`${styles['input-container']} ${props.disabled ? styles.disabled : ''}`}
        style={
          {
            '--icon-width': `${iconWidth ?? 24}px`,
          } as React.CSSProperties
        }
      >
        {props.icon && props.secondIcon && (
          <Icon
            name={props.secondIcon}
            className={`${styles.icon} ${styles['second-icon']} ${alignIcon ? styles[alignIcon] : ''} ${
              props.onClickIcon ? styles.click : ''
            }`}
            style={
              {
                '--icon-height': `${(iconHeight ?? 24) / 2}px`,
                '--icon-width': `${iconWidth ?? 24}px`,
              } as React.CSSProperties
            }
            width={iconWidth}
            height={iconHeight}
            onClick={() => props.onClickIcon && props.onClickIcon()}
          />
        )}
        {props.icon ? (
          <Icon
            name={props.icon}
            className={`${styles.icon} ${alignIcon ? styles[alignIcon] : ''} ${props.onClickIcon ? styles.click : ''}`}
            style={{ '--icon-height': `${(iconHeight ?? 24) / 2}px` } as React.CSSProperties}
            width={iconWidth}
            height={iconHeight}
            onClick={() => props.onClickIcon && props.onClickIcon()}
          />
        ) : null}

        <input
          onBlur={onFocusLost}
          type='text'
          name={props.name}
          className={`${styles.text} ${props.background ? styles[props.background] : styles.grey} ${
            !isValidState && !props.disabled ? styles.error : ''
          } ${props.icon && props.secondIcon ? styles[`text-icons-${alignIcon}`] : ''} ${
            props.showEllipsesOnOverflow ? styles.ellipses : ''
          }`}
          {...inputAttributes}
          ref={inputRef}
          aria-label={!props.label ? inputAttributes['aria-label'] ?? inputAttributes.placeholder ?? 'Text input' : undefined}
        />
      </div>

      {errorMessage && !isValidState && <p className={styles['error-text']}>{errorMessage}</p>}
    </div>
  );

  async function onFocusLost(e: React.FocusEvent<HTMLInputElement>) {
    if (validationCallback) {
      setIsValidState(await validationCallback(e.currentTarget.value));
    }

    // in case we have another blur callback
    onBlur && onBlur(e);
  }
});

export default Text;
